import { useQuery } from '@tanstack/vue-query';
import { createStorage } from '@aviata/storage';
import { appLocale, type blog } from 'shared';

import { api } from '@/shared/api';

import { BLOG_ARTICLE_STORAGE_KEY } from './config';

/**
 * Retrieves the blog article slug based on the current locale.
 * @returns {string} The slug for the blog article.
 */
function getBlogArticleSlug() {
  const isKKLocale =
    appLocale.lib.getAppLocale() === appLocale.config.locales.KK;
  const isRULocale =
    appLocale.lib.getAppLocale() === appLocale.config.locales.RU;

  if (isKKLocale) {
    return import.meta.env.VITE_BLOG_KK_ARTICLE_SLUG;
  }
  if (isRULocale) {
    return import.meta.env.VITE_BLOG_RU_ARTICLE_SLUG;
  }

  return import.meta.env.VITE_BLOG_EN_ARTICLE_SLUG;
}

/**
 * Extracts relevant article data from the blog article object.
 * @param {blog.types.BlogArticle} data - The blog article data.
 * @returns {Object} An object containing the article's category slug, preview image, read time, subtitle, title, and views.
 */
type StoredBlogArticle = blog.types.BlogArticle & { slug: string };

function getArticleData(data: blog.types.BlogArticle) {
  return {
    category_slug: data.category_slug,
    preview_image: data.preview_image,
    read_time_minutes: data.read_time_minutes,
    subtitle: data.subtitle,
    title: data.title,
    views: data.views,
  };
}

function isStoredBlogArticle(
  value: unknown,
  currentSlug: string,
): value is StoredBlogArticle {
  return (
    typeof value === 'object' &&
    value !== null &&
    'slug' in value &&
    (value as StoredBlogArticle).slug === currentSlug
  );
}

/**
 * Custom hook to fetch blog article data using tanstack/vue-query.
 * Checks localStorage for existing data before making an API request.
 * @returns {Object} An object containing error status, loading status, and article details.
 */
export function useFetchBlogArticle() {
  const currentSlug = getBlogArticleSlug();
  const storage = createStorage(localStorage);
  /**
   * Retrieves stored blog article details from localStorage.
   * Only used when the stored slug matches the current locale slug.
   */
  const parsed = storage.parse(
    BLOG_ARTICLE_STORAGE_KEY,
  ) as StoredBlogArticle | null;
  const storedDetails: blog.types.BlogArticle | undefined = isStoredBlogArticle(
    parsed,
    currentSlug,
  )
    ? parsed
    : undefined;

  const queryFn = async () => {
    if (storedDetails) {
      return storedDetails;
    }
    const data = await api.blog.getArticle(currentSlug);
    storage.stringify(BLOG_ARTICLE_STORAGE_KEY, {
      slug: currentSlug,
      ...getArticleData(data),
    });
    return data;
  };

  const {
    isError,
    isLoading,
    data: details,
  } = useQuery({
    queryKey: ['blog-article', currentSlug],
    queryFn,
    initialData: storedDetails,
  });

  return {
    isError,
    isLoading,
    details,
  };
}
